import { z } from 'zod';

// Credentials stay in the dashboard file for now (decision §12). The schemas mark the fields that
// hold them, so that logs, exports and a later move to a vault can find them without knowing each
// driver. The mark is zod metadata (`{ secret: true }` in the global registry).

/** Marks a schema as holding a credential. */
export function secret<T extends z.ZodType>(schema: T) : T {
    return schema.meta({ secret: true });
}

/** Whether a schema, or the schema inside an optional, nullable or default wrapper, is marked secret. */
export function isSecret(schema: z.ZodType) : boolean {
    let current: z.ZodType | undefined = schema;
    while (current) {
        if (z.globalRegistry.get(current)?.secret === true) {
            return true;
        }

        const inner: unknown = (current as { unwrap?: () => unknown }).unwrap?.();
        current = inner instanceof z.ZodType ? inner : undefined;
    }

    return false;
}

/** The keys of an object schema whose values are credentials. */
export function secretKeys(schema: z.ZodObject) : string[] {
    return Object.entries(schema.shape)
        .filter(([, field]) => isSecret(field as z.ZodType))
        .map(([key]) => key);
}

export const REDACTED = '***';

// A credential kept apart from the file is a placeholder in its field, `${secret:<name>}`
// (docs/specs/secrets.md SE-1). A field holds its value or a placeholder, in any store's file.
const PLACEHOLDER = /^\$\{secret:([^{}]{1,200})\}$/;

/** The placeholder that stands for the credential `name`. */
export function secretPlaceholder(name: string) : string {
    return `\${secret:${name}}`;
}

/** The name in a placeholder; undefined for anything else, a credential's value included. */
export function placeholderName(value: unknown) : string | undefined {
    return typeof value === 'string' ? PLACEHOLDER.exec(value)?.[1] : undefined;
}

/** A credential's name: its connection's id and its field's key, so that it stays the same across saves (SE-3). */
export function secretName(connection: string, key: string) : string {
    return `${connection}.${key}`;
}
